import React, { FC } from 'react';
import { useQuery } from '@apollo/client';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Link,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { productPriceHistoryTemplate } from 'apollo/templates/price-request';
import {
    PriceHistoryEntry,
    PriceHistorySource,
    ProductPriceHistory as ProductPriceHistoryModel,
} from 'models/price-request';
import { formatCurrency, formatDate } from 'utils/format';
import { formatPriceDelta, isWithdrawal, newestFirst } from './price-history-format';

type ProductPriceHistoryProps = {
    /** The other party: a lab id for a chain user, a chain id for a lab user. */
    counterpartId: string;
    actionId: string;
    actionSetId?: string;
    open: boolean;
    onClose: () => void;
};

/**
 * DD-3697 — one product's price timeline with one lab (productPriceHistory), newest
 * first: date, price, change in currency and %, and where the change came from. A
 * withdrawal is an event on the timeline; a later price continues the same timeline.
 * Opened from a request line and from both sides' price lists.
 */
const ProductPriceHistory: FC<ProductPriceHistoryProps> = ({
    counterpartId,
    actionId,
    actionSetId,
    open,
    onClose,
}: ProductPriceHistoryProps) => {
    const { t: translate } = useTranslation('price-requests');
    const { data, loading, error, refetch } = useQuery<{ productPriceHistory: ProductPriceHistoryModel }>(
        productPriceHistoryTemplate,
        {
            variables: { counterpartId, actionId, actionSetId },
            skip: !open,
            fetchPolicy: 'cache-and-network',
            notifyOnNetworkStatusChange: true,
        },
    );
    const history = data?.productPriceHistory;
    const entries = newestFirst(history?.entries ?? []);
    const showTiotandvard = entries.some((entry) => entry.values.tiotandvardPrice != null);

    const renderSource = (entry: PriceHistoryEntry) => {
        switch (entry.source) {
            case PriceHistorySource.INITIAL:
                return translate('HISTORY_SOURCE_INITIAL');
            case PriceHistorySource.MANUAL:
                return translate('HISTORY_SOURCE_MANUAL');
            case PriceHistorySource.LAB_DIRECT:
                return translate('HISTORY_SOURCE_LAB_DIRECT');
            case PriceHistorySource.REQUEST:
                return entry.priceRequest ? (
                    <Link component={RouterLink} to={`/price-requests/${entry.priceRequest}`} onClick={onClose}>
                        {entry.priceRequestNumber || translate('PRICE_REQUEST')}
                    </Link>
                ) : (
                    entry.priceRequestNumber || translate('PRICE_REQUEST')
                );
            default:
                return '';
        }
    };

    const renderChange = (entry: PriceHistoryEntry) => {
        if (isWithdrawal(entry)) return <Chip size="small" label={translate('HISTORY_WITHDRAWN')} />;
        if (!entry.previous) return translate('HISTORY_FIRST_PRICE');
        const delta = formatPriceDelta(entry.priceDelta, entry.pricePercent, entry.currency) || '—';
        return entry.previousEnabled === false ? `${delta} (${translate('HISTORY_PRICED_AGAIN')})` : delta;
    };

    const renderContent = () => {
        if (error && !history) {
            return (
                <Alert
                    severity="error"
                    action={
                        <Button color="inherit" size="small" onClick={() => refetch()}>
                            {translate('RETRY')}
                        </Button>
                    }
                >
                    {translate('HISTORY_LOAD_FAILED')}
                </Alert>
            );
        }
        if (loading && !history) {
            return (
                <Box display="flex" justifyContent="center" py={3}>
                    <CircularProgress size={28} aria-label={translate('HISTORY_LOADING')} />
                </Box>
            );
        }
        if (!history) return null;
        return (
            <>
                <Box display="flex" alignItems="center" gap={1} mb={2} flexWrap="wrap">
                    <Typography color="text.secondary">
                        {[history.lab.name, history.chain.name].filter(Boolean).join(' · ')}
                    </Typography>
                    {history.isWithdrawn ? (
                        <Chip size="small" label={translate('HISTORY_CURRENTLY_WITHDRAWN')} />
                    ) : (
                        history.current?.price != null && (
                            <Typography>
                                {translate('HISTORY_CURRENT_PRICE')}:{' '}
                                <strong>{formatCurrency(history.current.price, history.currency)}</strong>
                            </Typography>
                        )
                    )}
                </Box>
                {entries.length === 0 ? (
                    <Typography color="text.secondary">{translate('HISTORY_EMPTY')}</Typography>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{translate('HISTORY_DATE')}</TableCell>
                                    <TableCell align="right">{translate('HISTORY_PRICE')}</TableCell>
                                    {showTiotandvard && (
                                        <TableCell align="right">{translate('HISTORY_TIOTANDVARD_PRICE')}</TableCell>
                                    )}
                                    <TableCell>{translate('HISTORY_CHANGE')}</TableCell>
                                    <TableCell>{translate('HISTORY_DELIVERY_TIME')}</TableCell>
                                    <TableCell>{translate('HISTORY_SOURCE')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {entries.map((entry) => {
                                    const withdrawn = isWithdrawal(entry);
                                    return (
                                        <TableRow key={entry._id} data-testid="price-history-entry">
                                            <TableCell>{formatDate(entry.effectiveAt, 'date')}</TableCell>
                                            <TableCell align="right">
                                                {withdrawn ? '—' : formatCurrency(entry.values.price, entry.currency)}
                                            </TableCell>
                                            {showTiotandvard && (
                                                <TableCell align="right">
                                                    {withdrawn
                                                        ? '—'
                                                        : formatCurrency(entry.values.tiotandvardPrice, entry.currency)}
                                                </TableCell>
                                            )}
                                            <TableCell>{renderChange(entry)}</TableCell>
                                            <TableCell>{withdrawn ? '' : entry.values.deliveryTime ?? ''}</TableCell>
                                            <TableCell>
                                                {renderSource(entry)}
                                                {entry.changedByName && (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        component="div"
                                                    >
                                                        {entry.changedByName}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </>
        );
    };

    const title = [history?.actionNumber, history?.actionName].filter(Boolean).join(' ');

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth aria-labelledby="product-price-history-title">
            <DialogTitle id="product-price-history-title">
                {translate('HISTORY_TITLE')}
                {title ? `: ${title}` : ''}
            </DialogTitle>
            <DialogContent>{renderContent()}</DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{translate('CLOSE')}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default ProductPriceHistory;
